import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EndUserWithId } from '@app/core/interfaces/c5/end-user';
import { FeatureCode } from '@app/core/interfaces/c5/feature-code';
import { CallbackNumbers } from '@app/core/interfaces/callback-numbers';
import { FeatureProfileFeature } from '@app/core/interfaces/feature-profile';
import { Upload, UploadService } from '@app/shared/services/upload.service';
import { Utils } from '@app/shared/services/utils.service';
import { wrapError } from '@app/utils/rxjs';
import { StringMap } from '@app/utils/string-map';
import { Features } from '@business/feature/interfaces/features';
import { Feature } from '@business/feature/models/feature-model';
import { Observable } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class FeatureRepository {
  constructor(
    private http: HttpClient,
    private uploadService: UploadService,
    private utils: Utils
  ) {}

  add(feature: Feature): Observable<void> {
    return this.utils.apiUrl(`/features`).pipe(
      mergeMap((url) => this.http.post<void>(url, feature)),
      wrapError('feature-repository:add')
    );
  }

  distribute(feature: Feature, userId: string): Observable<void> {
    return this.utils
      .apiUrl(`/features/${feature.id}`, {
        userId,
      })
      .pipe(
        mergeMap((url) => this.http.put<void>(url, feature)),
        wrapError('feature-repository:distribute')
      );
  }

  get(id: number | string): Observable<Feature> {
    return this.utils.apiUrl(`/features/${id}`).pipe(
      mergeMap((url) => this.http.get<Feature>(url)),
      wrapError('feature-repository:get')
    );
  }

  getCallbackNumbers(): Observable<CallbackNumbers> {
    return this.utils.apiUrl('/callbackNumbers').pipe(
      mergeMap((url) => this.http.get<CallbackNumbers>(url)),
      wrapError('feature-repository:get-callback-numbers')
    );
  }

  getPeersWithFeature(featureName: string): Observable<EndUserWithId[]> {
    return this.utils.apiUrl(`/featureName/${featureName}`).pipe(
      mergeMap((url) => this.http.get<EndUserWithId[]>(url)),
      wrapError('feature-repository:get-peers-with-feature')
    );
  }

  list(): Observable<Features> {
    return this.utils.apiUrl('/features').pipe(
      mergeMap((url) => this.http.get<Features>(url)),
      wrapError('feature-repository:list')
    );
  }

  listFeatureCodes(): Observable<FeatureCode[]> {
    return this.utils.apiUrl('/featCodes').pipe(
      mergeMap((url) => this.http.get<FeatureCode[]>(url)),
      wrapError('feature-repository:list-feature-codes')
    );
  }

  removeVoiceMail(): Observable<void> {
    return this.utils.apiUrl(`/personalAnnouncement`).pipe(mergeMap((url) => this.http.delete<void>(url)));
  }

  resetFeatures(): Observable<Features> {
    return this.utils.apiUrl('/features/reset').pipe(
      mergeMap((url) => this.http.put<Features>(url, null)),
      wrapError('feature-repository:reset-features')
    );
  }

  update(feature: Feature): Observable<void> {
    return this.utils.apiUrl(`/features/${feature.id}`).pipe(
      mergeMap((url) => this.http.put<void>(url, feature)),
      wrapError('feature-repository:update')
    );
  }

  updateOptionalFeature(feature: FeatureProfileFeature, featureProfileId: number): Observable<void> {
    return this.utils.apiUrl(`/featureProfile/${featureProfileId}/feature`).pipe(
      mergeMap((url) => this.http.put<void>(url, feature)),
      wrapError('feature-repository:update-optional-feature')
    );
  }

  updateVoiceMail(file: File, data?: StringMap<any>): Observable<Upload<void>> {
    return this.utils.apiUrl('/personalAnnouncement').pipe(
      mergeMap((url) =>
        this.uploadService.post<void>({
          data,
          url,
          file,
        })
      )
    );
  }
}
